import React, { useState, useEffect } from 'react'
import { authService } from './authService'
import { AlertCircle, CheckCircle2, Lock, ArrowLeft, ShieldAlert } from 'lucide-react'

interface AcceptInvitationViewProps {
  onBackToLogin: () => void
}

type InvitationStatus = 'checking' | 'valid' | 'expired' | 'used' | 'revoked' | 'not_found'

export const AcceptInvitationView: React.FC<AcceptInvitationViewProps> = ({ onBackToLogin }) => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [tokenInput] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('token') || ''
  })
  const [invitationStatus, setInvitationStatus] = useState<InvitationStatus>(() => {
    const hash = window.location.hash
    const search = window.location.search
    if (search.includes('error_code=otp_expired') || hash.includes('error_code=otp_expired')) {
      return 'expired'
    }
    return tokenInput ? 'checking' : 'valid'
  })
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null)
  const [currentSessionEmail, setCurrentSessionEmail] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    authService.getCurrentSessionUser().then((user) => {
      if (user?.email) {
        setCurrentSessionEmail(user.email)
      }
    })
  }, [])

  useEffect(() => {
    if (!tokenInput || invitationStatus === 'expired') return

    authService.validateInvitationToken(tokenInput).then((res) => {
      setInvitationStatus(res.status)
      if (res.invitation?.email) {
        setInvitationEmail(res.invitation.email)
      }
    })
  }, [tokenInput, invitationStatus])

  const handleDirectAccept = async () => {
    if (!tokenInput) return
    setIsSubmitting(true)
    setErrorMsg(null)
    try {
      const { success: ok, error } = await authService.acceptInvitationDirect(tokenInput)
      if (!ok || error) {
        setErrorMsg(error?.message || 'Error al aceptar la invitación.')
      } else {
        setSuccess(true)
      }
    } catch {
      setErrorMsg('Error de comunicación con el servicio de acceso.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (password.length < 12) {
      setErrorMsg('La contraseña debe tener al menos 12 caracteres.')
      return
    }

    if (password !== confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden.')
      return
    }

    setIsSubmitting(true)
    try {
      if (tokenInput) {
        const { success: ok, error } = await authService.acceptInvitationWithPassword(tokenInput, password)
        if (!ok || error) {
          setErrorMsg(error?.message || 'Error al procesar la invitación.')
        } else {
          setSuccess(true)
        }
      } else {
        const { error } = await authService.updatePassword(password)
        if (error) {
          setErrorMsg(error.message || 'Error al guardar la contraseña.')
        } else {
          setSuccess(true)
        }
      }
    } catch {
      setErrorMsg('Error de comunicación con el servicio de acceso.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/branding/logo.png" alt="FEFA" className="auth-logo" />
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.25rem' }}>
            Activar Invitación
          </h1>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Configura tu contraseña para acceder a la plataforma de Team Spain.
          </p>
        </div>

        {/* ESTADO 1: INVITACIÓN EXPIRADA */}
        {invitationStatus === 'expired' && (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-warning-bg)',
                color: 'var(--status-warning-text)',
                border: '1px solid var(--status-warning-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Invitación expirada:</strong> El enlace de activación ha caducado por motivos de seguridad (validez máxima de 7 días). Solicita al cuerpo técnico o administrador que renueve tu invitación.
              </div>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              <ArrowLeft size={16} />
              <span>Volver al inicio de sesión</span>
            </button>
          </div>
        )}

        {/* ESTADO 2: INVITACIÓN YA UTILIZADA */}
        {invitationStatus === 'used' && (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-info-bg, #eff6ff)',
                color: 'var(--status-info-text, #1e40af)',
                border: '1px solid var(--status-info-border, #bfdbfe)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Invitación ya utilizada:</strong> Esta invitación ya fue aceptada previamente. Si ya configuraste tu contraseña, puedes iniciar sesión directamente con tu correo.
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              <span>Iniciar sesión</span>
            </button>
          </div>
        )}

        {/* ESTADO 3: INVITACIÓN REVOCADA */}
        {invitationStatus === 'revoked' && (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                border: '1px solid var(--status-danger-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <ShieldAlert size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Invitación revocada:</strong> Esta invitación ha sido cancelada o revocada por el administrador del equipo. Contacta con el staff para más información.
              </div>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              <ArrowLeft size={16} />
              <span>Volver al inicio de sesión</span>
            </button>
          </div>
        )}

        {/* ESTADO 4: INVITACIÓN NO ENCONTRADA */}
        {invitationStatus === 'not_found' && tokenInput && (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                border: '1px solid var(--status-danger-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Invitación no válida:</strong> No se ha encontrado ninguna invitación asociada al código facilitado.
              </div>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              <ArrowLeft size={16} />
              <span>Volver al inicio de sesión</span>
            </button>
          </div>
        )}

        {/* ESTADO 5: ÉXITO */}
        {success && (
          <div>
            <div
              style={{
                padding: '0.85rem',
                backgroundColor: 'var(--status-success-bg)',
                color: 'var(--status-success-text)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
              <span>Invitación aceptada y cuenta vinculada. Ya puedes acceder al sistema.</span>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={onBackToLogin}
            >
              Iniciar sesión
            </button>
          </div>
        )}

        {/* ESTADO 6: FORMULARIO DE ACTIVACIÓN (VALID O CHECKING) */}
        {!success && (invitationStatus === 'valid' || invitationStatus === 'checking') && (
          <form onSubmit={handleSubmit}>
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-text)',
                  border: '1px solid var(--status-danger-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span>{errorMsg}</span>
              </div>
            )}

            {invitationEmail && (
              <div
                style={{
                  padding: '0.6rem 0.75rem',
                  backgroundColor: 'var(--surface-subtle)',
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.78rem',
                  color: 'var(--text-secondary)',
                  marginBottom: '1rem',
                }}
              >
                Destinatario: <strong>{invitationEmail}</strong>
              </div>
            )}

            {currentSessionEmail && invitationEmail && currentSessionEmail.toLowerCase() === invitationEmail.toLowerCase() ? (
              <div
                style={{
                  marginBottom: '1.25rem',
                  padding: '0.85rem',
                  backgroundColor: 'var(--status-success-bg)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--status-success-border, #bbf7d0)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.82rem',
                    color: 'var(--status-success-text)',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                  <span>
                    Sesión iniciada como <strong>{currentSessionEmail}</strong>. Puedes vincular tu cuenta sin cambiar la contraseña actual.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                  onClick={handleDirectAccept}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Vinculando...' : 'Aceptar y vincular a mi cuenta activa'}
                </button>
                <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.5rem 0' }}>
                  — o si lo prefieres, define una nueva contraseña a continuación —
                </div>
              </div>
            ) : currentSessionEmail && invitationEmail && currentSessionEmail.toLowerCase() !== invitationEmail.toLowerCase() ? (
              <div
                style={{
                  marginBottom: '1.25rem',
                  padding: '0.85rem',
                  backgroundColor: 'var(--status-warning-bg)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--status-warning-border)',
                }}
              >
                <div style={{ fontSize: '0.82rem', color: 'var(--status-warning-text)', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    Has iniciado sesión como <strong>{currentSessionEmail}</strong>, pero esta invitación es para <strong>{invitationEmail}</strong>. Si deseas vincular la invitación a otra cuenta, cierra sesión primero.
                  </div>
                </div>
              </div>
            ) : null}

            <div className="form-group">
              <label className="form-label" htmlFor="invitation-password">
                Nueva Contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="invitation-password"
                  type="password"
                  className="form-input"
                  placeholder="Mínimo 12 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                <Lock
                  size={16}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="invitation-confirm">
                Confirmar Contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="invitation-confirm"
                  type="password"
                  className="form-input"
                  placeholder="Repite la contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                <Lock
                  size={16}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '0.5rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : 'Establecer contraseña y acceder'}
            </button>

            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={onBackToLogin}
                style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}
              >
                <ArrowLeft size={14} />
                <span>Volver al inicio de sesión</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
